import { GlassCard, GlassCardContent, GlassCardHeader } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Role } from "@/types/rbac";
import { Shield, Eye, Edit, Trash2, Users, Crown, Lock, Activity } from "lucide-react";
import { ActionsDropdown, ActionItem } from "@/components/ui/actions-dropdown";

interface RoleTemplateCardProps {
  role: Role;
  userCount?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  onView: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export default function RoleTemplateCard({ 
  role, 
  userCount = 0, 
  onEdit, 
  onDelete, 
  onView, 
  canEdit = true, 
  canDelete = true 
}: RoleTemplateCardProps) {
  const getRoleIcon = (roleName: string) => {
    const name = roleName.toLowerCase();
    if (name.includes('admin') || name.includes('super')) {
      return <Crown className="h-6 w-6 text-amber-600" />;
    }
    if (name.includes('manager') || name.includes('operator')) {
      return <Shield className="h-6 w-6 text-blue-600" />;
    }
    if (name.includes('viewer') || name.includes('read')) {
      return <Eye className="h-6 w-6 text-green-600" />;
    }
    return <Activity className="h-6 w-6 text-purple-600" />;
  };


const getActions = (): ActionItem[] => {
  const actions: ActionItem[] = [];

  if (!role.is_system) {
    if (canEdit && onEdit) {
      actions.push({
        label: "Edit Role",
        icon: Edit,
        onClick: onEdit,
      });
    }
    
    if (canDelete && onDelete) {
      actions.push({
        label: "Delete Role",
        icon: Trash2,
        onClick: onDelete,
        destructive: true,
      });
    }
  }

  return actions;
};

const actions = getActions();

return (
    <GlassCard 
      variant="default"
      className="cursor-pointer group"
      onClick={onView}
    >
      <GlassCardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br backdrop-blur-sm border transition-all duration-300 ${
              role.name.toLowerCase().includes('admin') || role.name.toLowerCase().includes('super')
                ? 'from-amber-500/10 to-amber-500/5 border-amber-500/20 group-hover:from-amber-500/20 group-hover:to-amber-500/10'
                : role.name.toLowerCase().includes('manager') || role.name.toLowerCase().includes('operator')
                ? 'from-blue-500/10 to-blue-500/5 border-blue-500/20 group-hover:from-blue-500/20 group-hover:to-blue-500/10'
                : role.name.toLowerCase().includes('viewer') || role.name.toLowerCase().includes('read')
                ? 'from-green-500/10 to-green-500/5 border-green-500/20 group-hover:from-green-500/20 group-hover:to-green-500/10'
                : 'from-purple-500/10 to-purple-500/5 border-purple-500/20 group-hover:from-purple-500/20 group-hover:to-purple-500/10'
            }`}>
              {getRoleIcon(role.name)}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{role.name}</h3>
              {role.is_system && (
                <Badge variant="outline" className="text-xs mt-1">
                  <Lock className="h-3 w-3 mr-1" />
                  System Role
                </Badge>
              )}
            </div>
          </div>
          
          {actions.length > 0 && (
            <div onClick={(e) => e.stopPropagation()}>
              <ActionsDropdown actions={actions} />
            </div>
          )}
        </div>
      </GlassCardHeader>
      
      <GlassCardContent className="pt-0">
        <div className="space-y-4">
          {role.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {role.description}
            </p>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>{userCount} user{userCount !== 1 ? 's' : ''} assigned</span>
            </div>
          </div>
          
          {/* Permission summary - placeholder for now */}
          <div className="flex flex-wrap gap-1 mt-2">
            <Badge variant="secondary" className="text-xs">
              Basic Access
            </Badge>
            {role.name.toLowerCase().includes('admin') && (
              <>
                <Badge variant="secondary" className="text-xs">
                  User Management
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  System Config
                </Badge>
              </>
            )}
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}